import { useState, useEffect, useMemo } from 'react';
import { Event, Member, Town } from '../types';
import { smsApi, settingsApi, templateApi, ApiResponse } from '../services/api';
import { useGlobalToast } from '../contexts/ToastContext';
import { useConfirmation } from './useConfirmation';

interface MemberSelection {
  filteredMembers: Member[];
  selectedMembers: string[];
  searchTerm: string;
  statusFilter: string;
  sortOption: string;
  isRefreshing: boolean;
}

interface SMSTemplate {
  type: string;
  message: string;
  adminContact: string;
  selectedEventId: string;
  eventOptions: Event[];
  availableTemplates: { id: string; name: string; content: string }[];
}

interface UseSMSComposerReturn extends MemberSelection, SMSTemplate {
  // Actions
  handleTemplateChange: (template: string) => void;
  updateMessage: (message: string) => void;
  handleSendSMS: () => Promise<void>;
  updateSearchTerm: (term: string) => void;
  updateStatusFilter: (filter: string) => void;
  updateSortOption: (option: string) => void;
  toggleMemberSelection: (memberId: string) => void;
  selectAllVisibleMembers: () => void;
  selectAllMembers: () => void;
  refreshData: () => Promise<void>;
  loadSMSData: () => Promise<void>;

  // Computed
  getRecipientCount: () => number;
  getEstimatedCost: () => number;
}

interface UseSMSComposerProps {
  members: Member[];
  towns: Town[];
  selectedTownId: string;
  events: Event[];
}

export const useSMSComposer = ({
  members,
  towns,
  selectedTownId,
  events
}: UseSMSComposerProps): UseSMSComposerReturn => {
  const { success, error } = useGlobalToast();
  // const _confirm = useConfirmation(); // Unused for now - bypassing confirmation

  // Member selection state
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortOption, setSortOption] = useState('name');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Template state
  const [messageType, setMessageType] = useState('general');
  const [message, setMessage] = useState('');
  const [adminContact, setAdminContact] = useState<string>('');
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [templates, setTemplates] = useState<Record<string, string>>({});
  const [availableTemplates, setAvailableTemplates] = useState<{ id: string; name: string; content: string }[]>([]);

  const effectiveTownId = selectedTownId; // Always use the explicitly selected town for SMS composer filtering

  // Compute filtered and sorted members
  const filteredMembers = useMemo(() => {
    return members
      .filter(member => {
        // Town filter
        if (effectiveTownId !== 'all' && member.townId !== effectiveTownId) {
          return false;
        }
        // Search filter
        if (searchTerm && !member.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !member.phone.includes(searchTerm)) {
          return false;
        }
        // Status filter
        if (statusFilter !== 'all') {
          if (member.status !== statusFilter) {
            return false;
          }
        } else {
          // If filter is 'all', exclude deceased members by default
          if (member.status === 'deceased') {
            return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        switch (sortOption) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'registrationDate':
            return new Date(a.registrationDate).getTime() - new Date(b.registrationDate).getTime();
          case 'town': {
            const townA = towns.find(t => t.id === a.townId)?.name || '';
            const townB = towns.find(t => t.id === b.townId)?.name || '';
            return townA.localeCompare(townB);
          }
          default:
            return 0;
        }
      });
  }, [members, effectiveTownId, searchTerm, statusFilter, sortOption, towns]);

  // Load SMS data
  const loadSMSData = async (): Promise<void> => {
    console.log('[useSMSComposer] Loading SMS data...');

    try {
      // Load settings and templates concurrently
      const [settingsRes, templatesRes] = await Promise.allSettled([
        settingsApi.get(),
        templateApi.getAll('sms')
      ]);

      // Handle settings
      if (settingsRes.status === 'fulfilled') {
        const res = settingsRes.value as ApiResponse<Record<string, unknown>>;
        const data = res?.data as {
          settings?: { adminContact?: string };
          adminContact?: string;
        } | undefined;
        const contact = data?.settings?.adminContact || data?.adminContact;
        if (typeof contact === 'string') setAdminContact(contact);
      }

      // Handle templates
      if (templatesRes.status === 'fulfilled') {
        const res = templatesRes.value as ApiResponse<Record<string, unknown>[]>;
        if (res.success && Array.isArray(res.data)) {
          const loadedTemplates = res.data.map((t: any) => ({
            id: t.id,
            name: t.name,
            content: t.content
          }));

          setAvailableTemplates(loadedTemplates);

          const templateMap: Record<string, string> = {};
          loadedTemplates.forEach((t: any) => {
            templateMap[t.id] = t.content;
          });

          // Add fallback for general
          templateMap['general'] = '';

          setTemplates(templateMap);
        } else {
          // If fetch fails, use fallback but keeping the original keys might break if UI expects new keys
          // asking strictly for 'sms' type might help
          console.warn('Failed to load dynamic templates, using empty map');
        }
      }
    } catch (err) {
      console.error('[useSMSComposer] Error loading SMS data:', err);
    }
  };

  // Handle template changes
  const handleTemplateChange = (template: string) => {
    console.log('[useSMSComposer] Template change:', { selectedTemplate: template });
    setMessageType(template);

    const templateMessage = templates[template] || '';

    // Replace admin contact for all templates
    let processedMessage = templateMessage.replace('[ADMIN_CONTACT]', adminContact);

    // For event and meeting templates, replace with selected event data
    if ((template === 'event' || template === 'meeting') && selectedEventId) {
      const selectedEvent = events.find(e => e.id === selectedEventId);
      if (selectedEvent) {
        processedMessage = processedMessage
          .replace('[DATE]', selectedEvent.date)
          .replace('[TIME]', selectedEvent.time)
          .replace('[VENUE]', selectedEvent.location)
          .replace('{event_title}', selectedEvent.title)
          .replace('{description}', selectedEvent.description);
      }
    }

    console.log('[useSMSComposer] Setting message to:', processedMessage);
    setMessage(processedMessage);
  };

  // Handle SMS sending
  const handleSendSMS = async (): Promise<void> => {
    const recipientCount = getRecipientCount();
    // const _estimatedCost = getEstimatedCost(); // Currently unused - saved for future implementation

    const isSendCredentials = messageType === 'send-credentials';

    console.log('[useSMSComposer] Send button clicked, validating...', {
      recipientCount,
      messageLength: message.length,
      messageType,
      selectedMembersCount: selectedMembers.length
    });

    try {
      console.log('[useSMSComposer] Skipping confirmation dialog...');

      // TEMPORARY: Skip confirmation dialog for testing
      const confirmed = true;

      console.log('[useSMSComposer] Confirmation result (bypassed):', confirmed);
    } catch (confirmError) {
      console.error('[useSMSComposer] Error showing confirmation:', confirmError);
      error('Failed to show confirmation dialog. Please try again.');
      return;
    }

    try {
      let response;

      if (isSendCredentials) {
        console.log('[useSMSComposer] Sending credentials to all members');
        response = await smsApi.sendCredentials();
      } else {
        const recipients = selectedMembers.map(id => ({ memberId: id }));
        console.log('[useSMSComposer] Sending bulk SMS:', {
          recipients: recipients.length,
          message: message.substring(0, 50) + '...'
        });

        response = await smsApi.sendBulk({
          message,
          recipients,
          type: messageType
        });
      }

      if (response.success) {
        const successMessage = isSendCredentials
          ? `Credentials sent successfully to ${recipientCount} members!`
          : `SMS sent successfully to ${recipientCount} recipients!`;
        success(successMessage);
        if (!isSendCredentials) {
          setMessage('');
          setSelectedMembers([]);
        }
      } else {
        error('Failed to send SMS: ' + (response.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('[useSMSComposer] Error sending SMS:', err);
      error('Failed to send SMS. Please try again.');
    }
  };

  // Member selection handlers
  const toggleMemberSelection = (memberId: string): void => {
    setSelectedMembers(prev => prev.includes(memberId)
      ? prev.filter(id => id !== memberId)
      : [...prev, memberId]
    );
  };

  const selectAllVisibleMembers = (): void => {
    const visibleIds = filteredMembers.map(m => m.id);
    setSelectedMembers(prev => {
      const currentSet = new Set(prev);
      const allSelected = visibleIds.every(id => currentSet.has(id));

      if (allSelected) {
        // Deselect all visible
        return prev.filter(id => !visibleIds.includes(id));
      } else {
        // Select all visible
        const newSelections = visibleIds.filter(id => !currentSet.has(id));
        return [...prev, ...newSelections];
      }
    });
  };

  const selectAllMembers = (): void => {
    const validMembers = members.filter(m => m.phone && m.phone.trim() !== '');
    const allIds = validMembers.map(m => m.id);
    setSelectedMembers(allIds);
  };

  // Update handlers
  const updateMessage = (newMessage: string): void => {
    setMessage(newMessage);
  };

  const updateSearchTerm = (term: string): void => {
    setSearchTerm(term);
  };

  const updateStatusFilter = (filter: string): void => {
    setStatusFilter(filter);
  };

  const updateSortOption = (option: string): void => {
    setSortOption(option);
  };

  const refreshData = async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      await loadSMSData();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Computed values
  const getRecipientCount = (): number => {
    return selectedMembers.length;
  };

  const getEstimatedCost = (): number => {
    return getRecipientCount() * 0.05;
  };

  // Effects
  useEffect(() => {
    loadSMSData();
  }, []);

  // Set default selected event when template changes
  useEffect(() => {
    if (messageType === 'event' || messageType === 'meeting') {
      const availableEvents = messageType === 'event'
        ? events
        : events.filter(e => e.type === 'Meeting');
      if (availableEvents.length > 0 && !selectedEventId) {
        setSelectedEventId(availableEvents[0].id);
      }
    } else {
      setSelectedEventId('');
      setSelectedMembers([]); // Clear selections when changing templates
    }
  }, [messageType, events, selectedEventId]);

  // Update message when selected event changes
  useEffect(() => {
    if ((messageType === 'event' || messageType === 'meeting') && selectedEventId) {
      const selectedEvent = events.find(e => e.id === selectedEventId);
      if (selectedEvent) {
        let templateMessage = templates[messageType] || '';
        templateMessage = templateMessage.replace('[ADMIN_CONTACT]', adminContact);
        templateMessage = templateMessage
          .replace('[DATE]', selectedEvent.date)
          .replace('[TIME]', selectedEvent.time)
          .replace('[VENUE]', selectedEvent.location)
          .replace('{event_title}', selectedEvent.title)
          .replace('{description}', selectedEvent.description);
        setMessage(templateMessage);
      }
    }
  }, [selectedEventId, messageType, events, templates, adminContact]);

  // Clear selected members when town selection changes
  useEffect(() => {
    setSelectedMembers([]);
  }, [selectedTownId]);

  // Auto-select all members when send-credentials template is selected
  useEffect(() => {
    if (messageType === 'send-credentials') {
      const allValidMembers = filteredMembers.filter(member => member.phone && member.phone.trim() !== '');
      const allIds = allValidMembers.map(member => member.id);
      setSelectedMembers(allIds);
    }
  }, [messageType, filteredMembers]);

  // Auto-refresh data when tab becomes active
  useEffect(() => {
    const handleFocus = () => {
      loadSMSData();
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const eventOptions = messageType === 'event'
    ? events
    : events.filter(e => e.type === 'Meeting');

  return {
    // Member selection
    filteredMembers,
    selectedMembers,
    searchTerm,
    statusFilter,
    sortOption,
    isRefreshing,

    // Template
    type: messageType,
    message,
    adminContact,
    selectedEventId,
    eventOptions,
    availableTemplates,

    // Actions
    handleTemplateChange,
    updateMessage,
    handleSendSMS,
    updateSearchTerm,
    updateStatusFilter,
    updateSortOption,
    toggleMemberSelection,
    selectAllVisibleMembers,
    selectAllMembers,
    refreshData,
    loadSMSData,

    // Computed
    getRecipientCount,
    getEstimatedCost,
  };
};
